import _ from "lodash";
import React from 'react';
import styled from "styled-components";
import classNames from "classnames";
import { ClientDashboardLoginEventDto, useClientDashboardLoginEvents } from "../hooks/ClientDashboardLoginEventsContext";
import { DateDisplay } from "../../../components";
import { VirtualTable } from "../../../components/tables";
import { StandardTableSortableHeader } from "../../../components/tables/StandardTable";
import { TableLoadingRow, TableRecordCountDisplayFooter } from "../../../components/tables/VirtualTable";

const LoginEventsTableHeader = ({ children, property, className } : Readonly<{ children: JSX.Element | string, className?: any | undefined, property: string }>) => {
    const { sort, sortTable } = useClientDashboardLoginEvents()!;
    const sortProperties = sort.split(',');
    const isHeaderSorted = _.some(sortProperties, (i: string) => i.includes(property));
    const headerDirection = isHeaderSorted && _.some(sortProperties, (i: string) => i.toLowerCase().includes('+desc')) ? "desc" : null;

    const handleClick = () => sortTable(property);

    return (
        <StandardTableSortableHeader
            className={classNames(className)}
            active={isHeaderSorted}
            direction={headerDirection}
            onClick={handleClick}
            isSticky={true}
        >
            {children}
        </StandardTableSortableHeader>
    );
};

const LoginEventsTableColumn = styled.td`
    width: ${({ width }) => width ? `${width}%` : ''};
    max-width: 0 !important;
    padding: 0.6rem !important;
    vertical-align: middle;
`;

const LoginEventsTableRow = ({ data, dateFormat = "DD/MM/YYYY", index, style } : Readonly<{ data: ClientDashboardLoginEventDto[], dateFormat?: string, index: number, style?: any }>) => {
    const { height } = style;

    // do not return a data row when the data has not yet been collected from the backend
    if (index >= data.length) {
        return <TableLoadingRow style={{ height }} />
    }

    const {
        eventDescription,
        message,
        loggedByIpAddress,
        loggedOnDateUtc
    } = data[index];

    return (
        <tr>
            <LoginEventsTableColumn width={17} style={{ height, whiteSpace: 'nowrap' }}>
                <p className="text-truncate m-0">
                    {eventDescription}
                </p>
            </LoginEventsTableColumn>
            <LoginEventsTableColumn width={12.5} style={{ whiteSpace: 'nowrap' }}>
                {loggedByIpAddress}
            </LoginEventsTableColumn>
            <LoginEventsTableColumn width={12.5} style={{ whiteSpace: 'nowrap' }}>
                <DateDisplay format="DD/MM/YYYY">{loggedOnDateUtc}</DateDisplay>
            </LoginEventsTableColumn>
            <LoginEventsTableColumn width={99}>
                <p className="text-truncate m-0">
                    {message}
                </p>
            </LoginEventsTableColumn>
        </tr>
    );
};

const LoginEventsTable = () => {
    const {
        clear: refreshTable,
        data,
        error,
        isLoading,
        isNextPageLoading,
        isNewRecords,
        isRowLoaded,
        loadMore,
        newRecordsSinceCache,
        totalCount
    } = useClientDashboardLoginEvents()!;

    const isNewRecordsDisplayed = isNewRecords && !isLoading && !isNextPageLoading;
    return (
        <VirtualTable
            enableScrollTracking={false}
            row={LoginEventsTableRow}
            header={(
                <thead>
                    <tr>
                        <LoginEventsTableHeader property={"event"}>Event</LoginEventsTableHeader>
                        <LoginEventsTableHeader property={"message"}>Message</LoginEventsTableHeader>
                        <LoginEventsTableHeader property={"loggedByIpAddress"}>Host</LoginEventsTableHeader>
                        <LoginEventsTableHeader property={"loggedOnDateUtc"}>Date</LoginEventsTableHeader>
                    </tr>
                </thead>
            )}
            footer={
                <TableRecordCountDisplayFooter
                    totalCount={totalCount}
                    isNewRecordsDisplayed={isNewRecordsDisplayed}
                    newRecordsSinceCache={newRecordsSinceCache}
                    reload={refreshTable}
                />
            }
            error={error}
            isLoading={isLoading}
            isItemLoaded={isRowLoaded}
            loadMoreItems={loadMore}
            itemSize={50}
            itemCount={totalCount}
            itemData={data}
        />
    )
};

export default LoginEventsTable;